import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthContext';
import { productionLogger } from '@/utils/production-logger';

// Generate a unique session ID for the browser session
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('visitor_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('visitor_session_id', sessionId);
  }
  return sessionId;
};

// Get basic user agent info safely
const getUserAgent = () => {
  try {
    return navigator.userAgent || 'Unknown';
  } catch {
    return 'Unknown';
  }
};

export const useVisitorTracking = () => {
  const location = useLocation();
  const { user } = useAuth();
  const sessionId = getSessionId();

  // Track page views and update session
  const trackPageView = useCallback(async (pathname: string) => {
    try {
      // Try to update existing session first
      const { error: updateError, data } = await supabase
        .from('user_sessions')
        .update({
          current_page: pathname,
          last_seen: new Date().toISOString(),
          is_active: true,
          user_agent: getUserAgent(),
        })
        .eq('session_id', sessionId)
        .select();

      // If no rows were updated (no existing session), try to insert new session
      if (!updateError && (!data || data.length === 0)) {
        const { error: insertError } = await supabase
          .from('user_sessions')
          .insert({
            session_id: sessionId,
            user_id: user?.id || null,
            current_page: pathname,
            last_seen: new Date().toISOString(),
            is_active: true,
            user_agent: getUserAgent(),
            started_at: new Date().toISOString()
          });
        
        // Ignore duplicate key errors (session already created by another request)
        if (insertError && insertError.code !== '23505') {
          throw insertError;
        }
      }
    } catch (error) {
      productionLogger.error('Failed to track page view:', error);
    }
  }, [sessionId, user?.id]);

  // Track product views specifically
  const trackProductView = useCallback(async (productId: string, pathname: string) => {
    try {
      // Track in product_views table
      await supabase
        .from('product_views')
        .insert({
          session_id: sessionId,
          user_id: user?.id || null,
          shopify_product_id: productId,
          page_url: pathname,
          created_at: new Date().toISOString()
        });
    } catch (error) {
      productionLogger.error('Failed to track product view:', error);
    }
  }, [sessionId, user?.id]);

  // Track page views automatically on route changes
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname, trackPageView]);

  // Track product views when on product pages
  useEffect(() => {
    // Check if we're on a product page and extract product ID
    const productMatch = location.pathname.match(/\/products\/([^\/]+)/);
    if (productMatch) {
      const productId = productMatch[1];
      trackProductView(productId, location.pathname);
    }
  }, [location.pathname, trackProductView]);

  // Keep session alive with periodic updates
  useEffect(() => {
    const keepAlive = setInterval(() => {
      trackPageView(location.pathname);
    }, 5 * 60 * 1000); // Update every 5 minutes

    return () => clearInterval(keepAlive);
  }, [location.pathname, trackPageView]);

  return {
    trackPageView,
    trackProductView,
    sessionId
  };
};
